import { useAuthStore } from '@/store/use-auth-store';
import type { AuthTenant } from '@/types/tenant';

const NON_TENANT_ROOT_SEGMENTS = new Set([
  'login',
  'soulmap-login',
  'teams-auth-start',
  'forgot-password',
  'change-password-success',
]);

/** First path segment when it represents a tenant slug (e.g. /acme/home → acme). */
export function extractTenantSlugFromPath(pathname: string): string | null {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment || NON_TENANT_ROOT_SEGMENTS.has(segment)) {
    return null;
  }
  return segment;
}

export function resolveTenantBySlug(
  slug: string | null | undefined,
  tenants: AuthTenant[] | null | undefined
): AuthTenant | null {
  if (!slug || !tenants?.length) {
    return null;
  }

  return tenants.find((tenant) => tenant.slug === slug) ?? null;
}

/** Resolve the active tenant from the current URL and the user's tenant list. */
export function getActiveTenant(pathname = window.location.pathname): AuthTenant | null {
  const slug = extractTenantSlugFromPath(pathname);
  const { tenants } = useAuthStore.getState();
  return resolveTenantBySlug(slug, tenants);
}

export function getActiveTenantId(pathname = window.location.pathname): number | null {
  return getActiveTenant(pathname)?.id ?? null;
}

/**
 * Tenant to send on session-level calls (/auth/me, logout). The backend rejects
 * a non-staff session with no valid tenant as "Invalid tenant", so when the URL
 * names none of the user's tenants (e.g. /login, a mistyped slug) fall back to
 * one they belong to.
 */
export function getSessionTenantId(pathname = window.location.pathname): number | null {
  return getActiveTenantId(pathname) ?? useAuthStore.getState().tenants?.[0]?.id ?? null;
}

export function getActiveTenantSlug(pathname = window.location.pathname): string | null {
  return extractTenantSlugFromPath(pathname);
}

const LAST_TENANT_SLUG_KEY = 'lift-last-tenant-slug';

/** Remember the tenant the user last worked in, so `/` can return them there. */
export function rememberLastTenantSlug(slug: string): void {
  try {
    localStorage.setItem(LAST_TENANT_SLUG_KEY, slug);
  } catch {
    // Storage unavailable (private mode); falling back to the selector is fine.
  }
}

function readLastTenantSlug(): string | null {
  try {
    return localStorage.getItem(LAST_TENANT_SLUG_KEY);
  } catch {
    return null;
  }
}

/**
 * Tenant to land on when the URL names none: the only one, or the last one used
 * if the user still belongs to it. Null means the user has to pick.
 */
export function resolveHomeTenantSlug(
  tenants: AuthTenant[] | null | undefined
): string | null {
  if (!tenants?.length) {
    return null;
  }
  if (tenants.length === 1) {
    return tenants[0].slug;
  }
  return resolveTenantBySlug(readLastTenantSlug(), tenants)?.slug ?? null;
}
